// src/components/helpdesk/TicketWorkOrderCard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Icon, useColorModeValue, HStack, VStack,
  Badge, Spinner, Tooltip, Progress, Divider, IconButton, Collapse,
  SimpleGrid, Skeleton, SkeletonText,
} from '@chakra-ui/react';
import {
  MdBuild, MdOpenInNew, MdRefresh, MdCheckCircle, MdSchedule,
  MdWarning, MdPerson, MdLocationOn, MdExpandMore, MdExpandLess,
  MdTimer, MdErrorOutline, MdFiberManualRecord,
} from 'react-icons/md';
import api from '../../services/api';

// ============================================================================
// CONSTANTS
// ============================================================================
const WO_STATUS_CONFIG = {
  pending:     { color: 'gray',   label: 'Pending',     icon: MdSchedule     },
  assigned:    { color: 'blue',   label: 'Assigned',    icon: MdPerson       },
  in_progress: { color: 'cyan',   label: 'In Progress', icon: MdBuild        },
  on_hold:     { color: 'yellow', label: 'On Hold',     icon: MdWarning      },
  completed:   { color: 'green',  label: 'Completed',   icon: MdCheckCircle  },
  cancelled:   { color: 'red',    label: 'Cancelled',   icon: MdErrorOutline },
};

const WO_PRIORITY_CONFIG = {
  critical: { color: 'red',    label: 'Critical' },
  high:     { color: 'orange', label: 'High'     },
  medium:   { color: 'blue',   label: 'Medium'   },
  low:      { color: 'gray',   label: 'Low'      },
};

const formatTimeAgo = (date) => {
  if (!date) return 'N/A';
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return new Date(date).toLocaleDateString();
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const TicketWorkOrderCard = ({ workOrderId }) => {
  // --------------------------------------------------------------------------
  // ALL hooks must be called before any early return
  // --------------------------------------------------------------------------
  const [workOrder, setWorkOrder]       = useState(null);
  const [isLoading, setIsLoading]       = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError]               = useState(null);
  const [isExpanded, setIsExpanded]     = useState(false);

  // --- ALL useColorModeValue calls hoisted unconditionally ---
  const cardBg          = useColorModeValue('orange.50',  'orange.900');
  const cardBorder      = useColorModeValue('orange.200', 'orange.700');
  const iconBg          = useColorModeValue('orange.100', 'orange.800');
  const labelColor      = useColorModeValue('orange.800', 'orange.100');
  const mutedColor      = useColorModeValue('orange.600', 'orange.300');
  const detailBg        = useColorModeValue('white',      'gray.800');
  const detailBorder    = useColorModeValue('orange.100', 'orange.800');
  const metaBg          = useColorModeValue('orange.25',  'gray.700');
  const progressTrack   = useColorModeValue('orange.100', 'orange.800');
  const errorBg         = useColorModeValue('red.50',     'red.900');
  const errorBorder     = useColorModeValue('red.200',    'red.700');
  const errorColor      = useColorModeValue('red.700',    'red.200');
  const skeletonStart   = useColorModeValue('orange.100', 'orange.800');
  const skeletonEnd     = useColorModeValue('orange.200', 'orange.700');

  // --------------------------------------------------------------------------
  // DATA FETCHING
  // --------------------------------------------------------------------------
  const fetchWorkOrder = useCallback(async (silent = false) => {
    if (!workOrderId) return;
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    setError(null);

    try {
      const response = await api.get(`/workorders/${workOrderId}`);
      const data = response.data?.workOrder || response.data?.data || response.data;
      setWorkOrder(data);
    } catch (err) {
      console.error('[TicketWorkOrderCard] Failed to fetch work order:', err);
      setError(err.response?.data?.message || 'Failed to load work order details.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [workOrderId]);

  useEffect(() => {
    fetchWorkOrder(false);
  }, [fetchWorkOrder]);

  // --------------------------------------------------------------------------
  // EARLY RETURN — after all hooks
  // --------------------------------------------------------------------------
  if (!workOrderId) return null;

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------
  const statusCfg   = WO_STATUS_CONFIG[workOrder?.status]   || WO_STATUS_CONFIG.pending;
  const priorityCfg = WO_PRIORITY_CONFIG[workOrder?.priority] || WO_PRIORITY_CONFIG.medium;
  const isComplete  = workOrder?.status === 'completed' || workOrder?.status === 'cancelled';

  // Completion progress — map status to a rough % for the progress bar
  const progressMap = {
    pending: 5, assigned: 20, in_progress: 60, on_hold: 60, completed: 100, cancelled: 100,
  };
  const progressValue   = progressMap[workOrder?.status] ?? 0;
  const progressScheme  = isComplete
    ? (workOrder?.status === 'completed' ? 'green' : 'red')
    : 'orange';

  // --------------------------------------------------------------------------
  // LOADING STATE
  // --------------------------------------------------------------------------
  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        p={4}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={cardBorder}
        mb={2}
      >
        <HStack spacing={4}>
          <Skeleton
            w="44px" h="44px"
            borderRadius="lg"
            startColor={skeletonStart}
            endColor={skeletonEnd}
          />
          <Box flex="1">
            <Skeleton h="16px" w="160px" mb={2} startColor={skeletonStart} endColor={skeletonEnd} />
            <Skeleton h="12px" w="260px" startColor={skeletonStart} endColor={skeletonEnd} />
          </Box>
          <Skeleton h="32px" w="140px" borderRadius="lg" startColor={skeletonStart} endColor={skeletonEnd} />
        </HStack>
      </Box>
    );
  }

  // --------------------------------------------------------------------------
  // ERROR STATE
  // --------------------------------------------------------------------------
  if (error) {
    return (
      <Box
        bg={errorBg}
        p={4}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={errorBorder}
        mb={2}
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={3}>
            <Icon as={MdErrorOutline} color={errorColor} boxSize={5} />
            <Box>
              <Text fontWeight="bold" color={errorColor} fontSize="sm">
                Work Order #{workOrderId}
              </Text>
              <Text fontSize="xs" color={errorColor} opacity={0.8}>{error}</Text>
            </Box>
          </HStack>
          <Tooltip label="Retry" hasArrow>
            <IconButton
              icon={<Icon as={MdRefresh} />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={() => fetchWorkOrder(false)}
              aria-label="Retry"
              borderRadius="lg"
            />
          </Tooltip>
        </Flex>
      </Box>
    );
  }

  // --------------------------------------------------------------------------
  // MAIN RENDER
  // --------------------------------------------------------------------------
  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={cardBorder}
      mb={2}
      overflow="hidden"
      transition="all 0.2s"
      _hover={{ boxShadow: 'md' }}
    >
      {/* Gradient top strip */}
      <Box
        h="3px"
        bgGradient={
          isComplete
            ? workOrder?.status === 'completed'
              ? 'linear(to-r, green.400, teal.400)'
              : 'linear(to-r, red.400, orange.400)'
            : 'linear(to-r, orange.400, yellow.400)'
        }
      />

      {/* Main summary row */}
      <Box p={4}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          {/* Left: icon + title + status */}
          <HStack spacing={4} flex="1" minW={0}>
            <Box
              p={2.5}
              bg={iconBg}
              borderRadius="lg"
              flexShrink={0}
            >
              <Icon
                as={statusCfg.icon}
                color={`${statusCfg.color}.500`}
                boxSize={6}
              />
            </Box>

            <Box minW={0} flex="1">
              <HStack spacing={2} mb={1} flexWrap="wrap">
                <Text fontWeight="bold" color={labelColor} fontSize="sm">
                  Field Ops Dispatched
                </Text>
                <Badge
                  colorScheme={statusCfg.color}
                  fontSize="xs"
                  borderRadius="full"
                  px={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon as={MdFiberManualRecord} boxSize={2} />
                  {statusCfg.label}
                </Badge>
                <Badge
                  colorScheme={priorityCfg.color}
                  variant="outline"
                  fontSize="xs"
                  borderRadius="full"
                  px={2}
                >
                  {priorityCfg.label}
                </Badge>
              </HStack>

              <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                {workOrder?.title
                  ? workOrder.title
                  : `Work Order #${workOrderId} is actively tracking this ticket`}
              </Text>
            </Box>
          </HStack>

          {/* Right: actions */}
          <HStack spacing={2} flexShrink={0}>
            {/* Expand toggle */}
            <Tooltip label={isExpanded ? 'Hide details' : 'Show details'} hasArrow>
              <IconButton
                icon={<Icon as={isExpanded ? MdExpandLess : MdExpandMore} />}
                size="sm"
                variant="ghost"
                colorScheme="orange"
                onClick={() => setIsExpanded(e => !e)}
                aria-label="Toggle details"
                borderRadius="lg"
              />
            </Tooltip>

            {/* Refresh */}
            <Tooltip label="Refresh status" hasArrow>
              <IconButton
                icon={<Icon as={MdRefresh} />}
                size="sm"
                variant="ghost"
                colorScheme="orange"
                onClick={() => fetchWorkOrder(true)}
                isLoading={isRefreshing}
                aria-label="Refresh"
                borderRadius="lg"
              />
            </Tooltip>

            {/* Open WO */}
            <Button
              size="sm"
              colorScheme="orange"
              variant="solid"
              rightIcon={<Icon as={MdOpenInNew} />}
              onClick={() => window.open(`/work-orders/${workOrderId}`, '_blank')}
              borderRadius="lg"
              boxShadow="sm"
              flexShrink={0}
            >
              View Live WO
            </Button>
          </HStack>
        </Flex>

        {/* Progress bar */}
        <Box mt={3}>
          <Flex justify="space-between" mb={1}>
            <Text fontSize="xs" color={mutedColor} fontWeight="600">
              Resolution Progress
            </Text>
            <Text fontSize="xs" color={mutedColor} fontWeight="700">
              {progressValue}%
            </Text>
          </Flex>
          <Progress
            value={progressValue}
            size="sm"
            colorScheme={progressScheme}
            borderRadius="full"
            bg={progressTrack}
            transition="width 0.5s ease"
          />
        </Box>
      </Box>

      {/* ================================================================ */}
      {/* EXPANDED DETAIL PANEL                                            */}
      {/* ================================================================ */}
      <Collapse in={isExpanded} animateOpacity>
        <Divider borderColor={cardBorder} />
        <Box p={4} bg={detailBg} borderTop="1px" borderColor={detailBorder}>
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>

            {/* WO ID */}
            <Box>
              <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={0.5}>
                Work Order ID
              </Text>
              <Text fontSize="sm" fontWeight="700" color={labelColor}>
                #{workOrderId}
              </Text>
            </Box>

            {/* Category */}
            {workOrder?.category && (
              <Box>
                <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={0.5}>
                  Category
                </Text>
                <Text fontSize="sm" fontWeight="600" color={labelColor}>
                  {workOrder.category}
                </Text>
              </Box>
            )}

            {/* Assigned Technician */}
            <Box>
              <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={0.5}>
                Assigned Technician
              </Text>
              <HStack spacing={1}>
                <Icon as={MdPerson} color={mutedColor} boxSize={4} />
                <Text fontSize="sm" fontWeight="600" color={labelColor}>
                  {workOrder?.assigned_to_name
                    || workOrder?.technician_name
                    || workOrder?.assigned_technician
                    || 'Unassigned'}
                </Text>
              </HStack>
            </Box>

            {/* Zone / Location */}
            {(workOrder?.zone_name || workOrder?.location) && (
              <Box>
                <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={0.5}>
                  Zone / Location
                </Text>
                <HStack spacing={1}>
                  <Icon as={MdLocationOn} color={mutedColor} boxSize={4} />
                  <Text fontSize="sm" fontWeight="600" color={labelColor}>
                    {workOrder.zone_name || workOrder.location}
                  </Text>
                </HStack>
              </Box>
            )}

            {/* Scheduled Date */}
            {workOrder?.scheduled_date && (
              <Box>
                <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={0.5}>
                  Scheduled Date
                </Text>
                <HStack spacing={1}>
                  <Icon as={MdTimer} color={mutedColor} boxSize={4} />
                  <Text fontSize="sm" fontWeight="600" color={labelColor}>
                    {formatDate(workOrder.scheduled_date)}
                  </Text>
                </HStack>
              </Box>
            )}

            {/* Created */}
            <Box>
              <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={0.5}>
                Created
              </Text>
              <Text fontSize="sm" fontWeight="600" color={labelColor}>
                {formatTimeAgo(workOrder?.created_at)}
              </Text>
            </Box>

            {/* Completed at */}
            {workOrder?.completed_at && (
              <Box>
                <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={0.5}>
                  Completed
                </Text>
                <Text fontSize="sm" fontWeight="600" color="green.500">
                  {formatDate(workOrder.completed_at)}
                </Text>
              </Box>
            )}
          </SimpleGrid>

          {/* Description */}
          {workOrder?.description && (
            <>
              <Divider my={3} borderColor={detailBorder} />
              <Box>
                <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={1}>
                  Description
                </Text>
                <Text fontSize="sm" color={labelColor} whiteSpace="pre-wrap" noOfLines={4}>
                  {workOrder.description}
                </Text>
              </Box>
            </>
          )}

          {/* Resolution Notes */}
          {workOrder?.resolution_notes && (
            <>
              <Divider my={3} borderColor={detailBorder} />
              <Box
                p={3}
                bg={metaBg}
                borderRadius="lg"
                borderLeft="3px solid"
                borderLeftColor="green.400"
              >
                <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={1}>
                  Resolution Notes
                </Text>
                <Text fontSize="sm" color={labelColor}>
                  {workOrder.resolution_notes}
                </Text>
              </Box>
            </>
          )}

          {/* Footer: last refreshed */}
          <Flex justify="flex-end" mt={3}>
            <Text fontSize="xs" color={mutedColor} opacity={0.7}>
              Last refreshed: {new Date().toLocaleTimeString()}
            </Text>
          </Flex>
        </Box>
      </Collapse>
    </Box>
  );
};

export default TicketWorkOrderCard;